import { Pencil, Eraser, Lasso, Hand, Undo2, Keyboard, MoreHorizontal } from 'lucide-react';
import clsx from 'clsx';
export type WorkingTool = 'pen' | 'eraser' | 'lasso' | 'move' | 'pan';
interface Props {
  fullScreen: boolean;
  blocked: boolean;
  locked: boolean;
  tool: WorkingTool;
  buttonClass: string;
  canUndo: boolean;
  showText: boolean;
  showMore: boolean;
  changeTool: (tool: WorkingTool) => void;
  undo: () => void;
  openWorking: () => void;
  toggleText: () => void;
  toggleMore: () => void;
}
const tools = [
  { key: 'pen', label: 'Pen', short: 'Write', Icon: Pencil },
  { key: 'eraser', label: 'Erase stroke', short: 'Erase', Icon: Eraser },
  { key: 'lasso', label: 'Select strokes', short: 'Select', Icon: Lasso },
  { key: 'pan', label: 'Move page', short: 'Move', Icon: Hand },
] as const;
export function WorkingToolbar({
  fullScreen,
  blocked,
  locked,
  tool,
  buttonClass,
  canUndo,
  showText,
  showMore,
  changeTool,
  undo,
  openWorking,
  toggleText,
  toggleMore,
}: Props) {
  const touchButton = clsx(
    buttonClass,
    fullScreen && 'h-14 w-full flex-col gap-1 rounded-xl text-[9px] md:h-11 md:w-11 md:flex-row',
  );
  return (
    <div
      role="toolbar"
      aria-label="Working tools"
      className={clsx(
        'bg-canvas-pure',
        fullScreen
          ? 'absolute inset-x-0 bottom-0 z-30 grid grid-cols-6 gap-0.5 border-t border-hairline px-2 pt-2 pb-[max(.5rem,env(safe-area-inset-bottom))] shadow-lg md:inset-x-auto md:bottom-[max(1rem,env(safe-area-inset-bottom))] md:left-1/2 md:flex md:-translate-x-1/2 md:items-center md:rounded-2xl md:border md:p-[6px]'
          : 'relative z-10 mx-3 my-3 flex items-center justify-center gap-1 self-center',
      )}
    >
      {fullScreen &&
        tools.map(({ key, label, short, Icon }) => (
          <button
            key={key}
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={tool === key || (key === 'lasso' && tool === 'move')}
            disabled={key !== 'pan' && blocked}
            className={clsx(
              touchButton,
              key === 'lasso' && 'hidden md:inline-flex',
              (tool === key || (key === 'lasso' && tool === 'move')) &&
                'bg-wash-sky text-brand-sky',
            )}
            onClick={() => changeTool(key)}
          >
            <Icon size={20} />
            <span className="font-bold md:hidden">{short}</span>
          </button>
        ))}
      {fullScreen && (
        <button
          type="button"
          aria-label="Undo working"
          title="Undo"
          disabled={blocked || !canUndo}
          className={touchButton}
          onClick={undo}
        >
          <Undo2 size={20} />
          <span className="font-bold md:hidden">Undo</span>
        </button>
      )}
      {!fullScreen && (
        <button
          type="button"
          data-open-working
          className="flex min-h-11 items-center gap-2 rounded-xl bg-wash-sky px-4 text-sm font-bold text-ink"
          onClick={openWorking}
        >
          <Pencil size={18} />
          {locked ? 'View your working' : 'Write your working'}
        </button>
      )}
      <button
        type="button"
        aria-label={showText ? 'Hide text steps' : 'Open text steps'}
        title="Text steps"
        aria-expanded={showText}
        className={clsx(touchButton, showText && 'bg-wash-sky text-brand-sky')}
        onClick={toggleText}
      >
        <Keyboard size={20} />
        <span className={clsx('font-bold md:hidden', !fullScreen && 'hidden')}>Text</span>
      </button>
      <button
        type="button"
        aria-label="More tools"
        title="Pen, paper and export"
        aria-expanded={showMore}
        className={clsx(touchButton, showMore && 'bg-wash-sky text-brand-sky')}
        onClick={toggleMore}
      >
        <MoreHorizontal size={22} />
        <span className={clsx('font-bold md:hidden', !fullScreen && 'hidden')}>More</span>
      </button>
    </div>
  );
}
